import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adminListUsers, adminUpdateUser, adminDeleteUser, adminListCompanies, adminCreateCompany, adminUpdateCompany, adminDeleteCompany, adminUploadCompanyLogo, adminDeleteCompanyLogo, adminListDatabases, adminAssignDatabaseCompany, getChartLibrarySettings, putChartLibrarySettings, getDatabases } from '../services/api';
import { ProfileIcon, ChartListIcon } from '../components/ui/Icons';
import { LayoutGrid, LineChart as LineIcon, BarChart3 as BarIcon, PieChart as PieIcon, Radar, Layers, Table2, Search, ArrowLeft, Users, ChevronDown, ShieldCheck, Building2, Database, Plus, RefreshCw, X, Save, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { PromptModal } from '../components/ui/PromptModal';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { useAuth } from '../context/AuthContext';
import { WriteGuard, ReadOnlyBanner } from '../components/auth/WriteGuard';
import { ChartCard } from '../components/charts';
import { chartsData } from '../data/chartsData';
import { Header } from '../components/layout/Header';
import { useToast } from '../context/ToastContext';
import '../utils/reportChartSetup';

export function Settings() {
    const { t } = useTranslation(['page-settings', 'common']);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const initialTab = searchParams.get('tab') || 'profile';
    const [activeTab, setActiveTab] = useState(initialTab);
    const [activeSubTab, setActiveSubTab] = useState(searchParams.get('sub') || 'roles');
    const { user: authUser, role: userRole, isAdmin, loading: authLoading } = useAuth();
    const user = authUser || { username: 'User', role: 'viewer' };

    // Role Management (companies / users / databases / permissions) is admin-only.
    const allTabs = [
        { id: 'profile', name: t('tabs.profile.name'), icon: ProfileIcon, description: t('tabs.profile.description') },
        { id: 'charts', name: t('tabs.charts.name'), icon: ChartListIcon, description: t('tabs.charts.description') },
        {
            id: 'role-management',
            name: t('tabs.roleManagement.name'),
            icon: Users,
            description: t('tabs.roleManagement.description'),
            adminOnly: true,
            subTabs: [
                { id: 'companies', name: t('tabs.companies.name'), icon: Building2, description: t('tabs.companies.description') },
                { id: 'users', name: t('tabs.users.name'), icon: Users, description: t('tabs.users.description') },
                { id: 'databases', name: t('tabs.databases.name'), icon: Database, description: t('tabs.databases.description') }
            ]
        },
    ];
    const tabs = allTabs.filter((t) => !t.adminOnly || isAdmin);
    // Non-admins must never land on the admin-only Role Management section,
    // even via a ?tab=role-management URL. Wait for the live role to resolve so
    // an admin never briefly sees the "access denied" card on load.
    const roleManagementBlocked = activeTab === 'role-management' && !isAdmin && !authLoading;

    useEffect(() => {
        const tab = searchParams.get('tab');
        const sub = searchParams.get('sub');
        if (tab && tab !== activeTab) {
            setActiveTab(tab);
        }
        if (sub && sub !== activeSubTab) {
            setActiveSubTab(sub);
        }
    }, [searchParams, activeTab, activeSubTab]);

    const handleTabChange = (id) => {
        setActiveTab(id);
        const nextParams = { tab: id };
        if (id === 'role-management') {
            nextParams.sub = 'companies';
            setActiveSubTab('companies');
        }
        const dbId = searchParams.get('database_id');
        if (id === 'charts' && dbId) {
            nextParams.database_id = dbId;
        }
        setSearchParams(nextParams);
    };

    const handleSubTabChange = (subId) => {
        setActiveSubTab(subId);
        setSearchParams({ tab: activeTab, sub: subId });
    };

    const activeTabMeta = tabs.find((t) => t.id === activeTab);
    const pageSubtitle = activeTab === 'role-management'
        ? activeTabMeta?.subTabs?.find((s) => s.id === activeSubTab)?.description
        : activeTabMeta?.description;

    return (
        <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
            <Header />
            <div className="flex flex-1 overflow-hidden">
                {/* Settings Sidebar */}
                <div className="w-64 bg-white border-r border-gray-200 flex flex-col flex-shrink-0 animate-in slide-in-from-left duration-500">
                    <div className="px-6 pt-6 pb-4 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="p-2.5 rounded-xl bg-gray-50 text-gray-500 hover:text-primary-600 hover:bg-primary-50 transition-all active:scale-95 border border-gray-100 shadow-sm flex items-center justify-center"
                                title={t('goBack')}
                                style={{ height: '36px', width: '36px' }}
                            >
                                <ArrowLeft className="w-4 h-4" />
                            </button>
                            <div>
                                <h1 className="text-lg font-black text-gray-900 tracking-tight leading-none">{t('title')}</h1>
                                <p className="text-[9px] text-gray-400 mt-1 font-bold uppercase tracking-wider">{t('subtitle')}</p>
                            </div>
                        </div>
                    </div>

                    <nav className="px-3 py-4 space-y-1 flex-1 overflow-y-auto">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabChange(tab.id)}
                                    className={`w-full flex items-center space-x-3 px-3 py-[0.75rem] rounded-lg transition-colors ${activeTab === tab.id
                                        ? 'bg-primary-50 text-primary-600'
                                        : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                >
                                    <tab.icon className={`w-[18px] h-[18px] flex-shrink-0 transition-colors ${activeTab === tab.id ? 'text-primary-600' : 'text-gray-500 group-hover:text-gray-900'
                                        }`} />
                                    <span className="font-medium text-sm whitespace-nowrap">{tab.name}</span>
                                </button>
                            ))}
                    </nav>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                    <main className="flex-1 overflow-auto scrollbar-hide">
                        <div className="page-shell">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                                <div>
                                    <h1 className="page-title">{activeTabMeta?.name}</h1>
                                    {pageSubtitle && (
                                        <p className="page-subtitle">{pageSubtitle}</p>
                                    )}
                                </div>

                                {activeTab === 'role-management' && isAdmin && (
                                    <div className="flex items-center gap-3 shrink-0">
                                        <SegmentedControl
                                            activeId={activeSubTab}
                                            onChange={handleSubTabChange}
                                            items={activeTabMeta.subTabs.map((s) => ({
                                                id: s.id,
                                                label: s.name,
                                                icon: s.icon,
                                            }))}
                                        />
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => window.location.reload()}
                                            className="flex items-center gap-2"
                                        >
                                            <RefreshCw className="w-3.5 h-3.5" />
                                            {t('refresh')}
                                        </Button>
                                    </div>
                                )}
                            </div>

                            <Card className={(activeTab === 'charts' || activeTab === 'role-management') ? 'min-h-[800px] border-none shadow-none bg-transparent' : 'min-h-[600px]'}>
                                {activeTab === 'profile' && <ProfileSection user={user} />}
                                {activeTab === 'charts' && <ChartLibrarySection />}
                                {roleManagementBlocked && (
                                    <div className="flex flex-col items-center justify-center py-24 text-center">
                                        <ShieldCheck className="w-10 h-10 text-gray-300 mb-3" />
                                        <p className="text-sm font-semibold text-gray-700">{t('adminAccessRequired')}</p>
                                        <p className="text-xs text-gray-500 mt-1 max-w-sm">
                                            {t('adminAccessRequiredBody', { role: userRole })}
                                        </p>
                                    </div>
                                )}
                                {activeTab === 'role-management' && isAdmin && activeSubTab === 'companies' && <CompaniesSection />}
                                {activeTab === 'role-management' && isAdmin && activeSubTab === 'users' && <UsersSection currentUsername={user.username} />}
                                {activeTab === 'role-management' && isAdmin && activeSubTab === 'databases' && <DatabasesSection />}
                            </Card>
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
}

function ProfileSection({ user }) {
    const { t } = useTranslation('page-settings');
    return (
        <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                    label={t('profile.username')}
                    value={user.username}
                    readOnly
                    className="flex-1"
                />
                <Input
                    label={t('profile.email')}
                    value={user.email || '-'}
                    readOnly
                    className="flex-1"
                />
                <Input
                    label={t('profile.fullName')}
                    value={user.name || '-'}
                    readOnly
                    className="flex-1"
                />
                <Input
                    label={t('profile.company')}
                    value={user.company_name || '-'}
                    readOnly
                    className="flex-1"
                />
            </div>
        </div>
    );
}


function ChartLibrarySection() {
    const { t } = useTranslation('page-settings');
    const [activeCategory, setActiveCategory] = useState('All Examples');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const databaseId = searchParams.get('database_id');
    const [databases, setDatabases] = useState([]);
    const [databasesLoading, setDatabasesLoading] = useState(true);
    const [reportSettings, setReportSettings] = useState({});
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [settingsError, setSettingsError] = useState(null);
    const [saveState, setSaveState] = useState('idle');
    const navigate = useNavigate();

    const categories = [
        { id: 'All Examples', label: t('chartLibrary.categories.allExamples'), icon: LayoutGrid },
        { id: 'Line', label: t('chartLibrary.categories.line'), icon: LineIcon },
        { id: 'Bar', label: t('chartLibrary.categories.bar'), icon: BarIcon },
        { id: 'Pie / Doughnut', label: t('chartLibrary.categories.pieDoughnut'), icon: PieIcon },
        { id: 'Radar / Polar', label: t('chartLibrary.categories.radarPolar'), icon: Radar },
        { id: 'Table', label: t('chartLibrary.categories.table'), icon: Table2 },
        { id: 'Special Styles', label: t('chartLibrary.categories.specialStyles'), icon: Layers },
    ];

    const filteredCharts = chartsData.filter(chart => {
        const matchesCategory = activeCategory === 'All Examples' || chart.category === activeCategory;
        const matchesSearch = chart.title.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    useEffect(() => {
        if (!databaseId) {
            setReportSettings({});
            setSettingsError(null);
            setSettingsLoading(false);
            return;
        }
        let cancelled = false;
        setSettingsLoading(true);
        setSettingsError(null);
        (async () => {
            try {
                const res = await getChartLibrarySettings(databaseId);
                const settings = res.data?.data?.settings ?? {};
                if (!cancelled) {
                    setReportSettings(typeof settings === 'object' && settings !== null ? settings : {});
                }
            } catch (e) {
                if (!cancelled) {
                    setSettingsError(e.response?.data?.detail || e.message || t('chartLibrary.failedToLoadSettings'));
                    setReportSettings({});
                }
            } finally {
                if (!cancelled) setSettingsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [databaseId, t]);

    useEffect(() => {
        let cancelled = false;
        setDatabasesLoading(true);
        (async () => {
            try {
                const res = await getDatabases();
                const list = res.data?.data?.databases ?? [];
                if (!cancelled) setDatabases(Array.isArray(list) ? list : []);
            } catch {
                if (!cancelled) setDatabases([]);
            } finally {
                if (!cancelled) setDatabasesLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleDatabaseChange = (e) => {
        const v = e.target.value;
        const next = new URLSearchParams(searchParams);
        next.set('tab', 'charts');
        if (v) next.set('database_id', v);
        else next.delete('database_id');
        setSearchParams(next, { replace: true });
    };

    const handleReportNoteChange = (chartId, value) => {
        setReportSettings((prev) => ({ ...prev, [chartId]: value }));
    };

    const handleSaveSettings = async () => {
        if (!databaseId) return;
        setSaveState('saving');
        setSettingsError(null);
        try {
            await putChartLibrarySettings(databaseId, { settings: reportSettings });
            setSaveState('saved');
            setTimeout(() => setSaveState('idle'), 2000);
        } catch (e) {
            setSaveState('error');
            setSettingsError(e.response?.data?.detail || e.message || t('chartLibrary.saveFailed'));
        }
    };

    const handleChartClick = (id) => {
        const qs = databaseId ? `?database_id=${encodeURIComponent(databaseId)}` : '';
        navigate(`/chart-library/${id}${qs}`);
    };

    return (
        <div className="flex flex-col h-full animate-in fade-in duration-500 gap-6">
            <div className="flex flex-col gap-4">
                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                    <SegmentedControl
                        activeId={activeCategory}
                        onChange={setActiveCategory}
                        items={categories.map((cat) => ({
                            id: cat.id,
                            label: cat.label,
                            icon: cat.icon,
                        }))}
                        className="flex-wrap self-start max-w-full shrink-0"
                    />
                    <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4 min-w-0 xl:ml-auto xl:justify-end">
                        <div className="relative w-full lg:w-56 shrink-0">
                            <Input
                                placeholder={t('chartLibrary.searchPlaceholder')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full"
                            />
                            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0">
                            <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 shrink-0">
                                <Database className="w-4 h-4 text-primary-600" />
                                {t('chartLibrary.databaseForReportNotes')}
                            </label>
                            <select
                                value={databaseId || ''}
                                onChange={handleDatabaseChange}
                                disabled={databasesLoading}
                                className="w-full sm:min-w-[10rem] sm:max-w-[14rem] text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-900 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-500/15 disabled:opacity-60"
                            >
                                <option value="">
                                    {databasesLoading ? t('chartLibrary.loadingDatabases') : t('chartLibrary.selectDatabase')}
                                </option>
                                {databases.map((db) => (
                                    <option key={db.id} value={String(db.id)}>
                                        {db.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {databaseId && (
                            <WriteGuard>
                                <Button
                                    type="button"
                                    onClick={handleSaveSettings}
                                    disabled={saveState === 'saving' || settingsLoading}
                                    className="inline-flex items-center justify-center gap-2 shrink-0"
                                >
                                    {saveState === 'saving' ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Save className="w-4 h-4" />
                                    )}
                                    {saveState === 'saved'
                                        ? t('chartLibrary.saved')
                                        : saveState === 'error'
                                          ? t('chartLibrary.retrySave')
                                          : t('chartLibrary.saveNotes')}
                                </Button>
                            </WriteGuard>
                        )}
                    </div>
                </div>
                <ReadOnlyBanner />
                {databaseId && settingsLoading && (
                    <p className="text-gray-500 text-xs flex items-center gap-2 -mt-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        {t('chartLibrary.loadingSavedNotes')}
                    </p>
                )}
                {settingsError && (
                    <p className="text-red-600 text-xs -mt-2">{settingsError}</p>
                )}
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-8 auto-rows-min">
                    {filteredCharts.length > 0 ? (
                        filteredCharts.map((chart) => {
                            const isFullWidth =
                                chart.type === 'DASHBOARD' ||
                                chart.type === 'KPI' ||
                                chart.type === 'TABLE';
                            return (
                                <div
                                    key={chart.id}
                                    className={isFullWidth ? 'col-span-full' : ''}
                                >
                                    <ChartCard
                                        chart={chart}
                                        onClick={handleChartClick}
                                        databaseId={databaseId || undefined}
                                        reportNote={reportSettings[chart.id] ?? ''}
                                        onReportNoteChange={databaseId ? handleReportNoteChange : undefined}
                                    />
                                </div>
                            );
                        })
                    ) : (
                        <div className="col-span-full py-24 text-center">
                            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Search className="w-8 h-8 text-gray-300" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900">{t('chartLibrary.noChartsFound')}</h3>
                            <p className="text-gray-500 font-medium">{t('chartLibrary.tryAdjustingSearch')}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function CompaniesSection() {
    const { t } = useTranslation(['page-settings', 'common']);
    const [companies, setCompanies] = useState([]);
    const [newCompanyName, setNewCompanyName] = useState('');

    const [promptModal, setPromptModal] = useState({ isOpen: false, id: null, name: '' });
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null });

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        try {
            const res = await adminListCompanies();
            setCompanies(res.data?.data?.companies || []);
        } catch (e) { }
    };

    const handleCreate = async () => {
        if (!newCompanyName.trim()) return;
        try {
            await adminCreateCompany({ name: newCompanyName.trim() });
            setNewCompanyName('');
            fetchCompanies();
        } catch (e) { }
    };

    const handleUpdate = async (name) => {
        try {
            await adminUpdateCompany(promptModal.id, { name });
            setPromptModal({ isOpen: false, id: null, name: '' });
            fetchCompanies();
        } catch (e) { }
    };

    const handleDelete = async () => {
        try {
            await adminDeleteCompany(confirmModal.id);
            setConfirmModal({ isOpen: false, id: null });
            fetchCompanies();
        } catch (e) { }
    };

    const handleLogoUpload = async (id, file) => {
        if (!file) return;
        try {
            await adminUploadCompanyLogo(id, file);
            fetchCompanies();
        } catch (e) { }
    };

    const handleLogoDelete = async (id) => {
        try {
            await adminDeleteCompanyLogo(id);
            fetchCompanies();
        } catch (e) { }
    };

    return (
        <div className="p-8">
            <div className="flex gap-4 mb-8">
                <Input
                    placeholder={t('page-settings:companies.newCompanyNamePlaceholder')}
                    value={newCompanyName}
                    onChange={(e) => setNewCompanyName(e.target.value)}
                    className="flex-1"
                />
                <Button onClick={handleCreate} className="h-[42px] px-8">
                    {t('common:add')}
                </Button>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead>
                        <tr className="bg-gray-50 border-y border-gray-200">
                            <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('page-settings:companies.logo')}</th>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('common:name')}</th>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider text-right">{t('common:actions')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {companies.map((c) => (
                            <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden">
                                            {c.logo_path ? (
                                                <img src={`/logo_folder/${c.logo_path}`} className="w-full h-full object-contain" alt="" />
                                            ) : (
                                                <span className="text-gray-400 font-bold text-lg">{c.name.charAt(0).toUpperCase()}</span>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <label className="cursor-pointer px-2 py-1 bg-white border border-gray-200 rounded text-[10px] font-bold text-gray-600 hover:bg-gray-50 transition-all text-center">
                                                {c.logo_path ? t('page-settings:companies.change') : t('page-settings:companies.upload')}
                                                <input type="file" className="hidden" onChange={(e) => handleLogoUpload(c.id, e.target.files[0])} />
                                            </label>
                                            {c.logo_path && (
                                                <button onClick={() => handleLogoDelete(c.id)} className="px-2 py-1 bg-red-50 text-[10px] font-bold text-red-600 hover:bg-red-100 rounded transition-all">
                                                    {t('page-settings:companies.remove')}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-900">{c.name}</div>
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setPromptModal({ isOpen: true, id: c.id, name: c.name })}
                                        >
                                            {t('page-settings:companies.rename')}
                                        </Button>
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => setConfirmModal({ isOpen: true, id: c.id })}
                                        >
                                            {t('common:delete')}
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <PromptModal
                isOpen={promptModal.isOpen}
                onClose={() => setPromptModal({ isOpen: false, id: null, name: '' })}
                onConfirm={handleUpdate}
                title={t('page-settings:companies.renameTitle')}
                message={t('page-settings:companies.renameMessage')}
                initialValue={promptModal.name}
            />

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, id: null })}
                onConfirm={handleDelete}
                title={t('page-settings:companies.deleteTitle')}
                message={t('page-settings:companies.deleteMessage')}
                confirmText={t('common:delete')}
                variant="danger"
            />
        </div>
    );
}

function UsersSection({ currentUsername }) {
    const { t } = useTranslation(['page-settings', 'common']);
    const { showToast } = useToast();
    const [users, setUsers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [uRes, cRes] = await Promise.all([adminListUsers(), adminListCompanies()]);
            setUsers(uRes.data?.data?.users || []);
            setCompanies(cRes.data?.data?.companies || []);
        } catch (e) { }
    };

    const handleUpdateUser = async (username, data) => {
        if (
            username === currentUsername
            && data.role && data.role !== 'admin'
        ) {
            showToast(t('page-settings:users.cannotChangeOwnAdminRole'), 'error');
            return;
        }
        try {
            await adminUpdateUser(username, data);
            fetchData();
        } catch (e) {
            const detail = e.response?.data?.detail;
            showToast(typeof detail === 'string' ? detail : t('page-settings:users.failedToUpdateUser'), 'error');
        }
    };

    const handleDeleteUser = async () => {
        try {
            await adminDeleteUser(confirmModal.id);
            setConfirmModal({ isOpen: false, id: null });
            fetchData();
        } catch (e) { }
    };

    return (
        <div className="p-10 animate-in fade-in duration-500">
            <div className="overflow-x-auto -mx-10">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50/50 border-y border-gray-100">
                            <th className="px-10 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">{t('page-settings:users.username')}</th>
                            <th className="px-10 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">{t('page-settings:users.activeCompany')}</th>
                            <th className="px-10 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">{t('page-settings:users.companyAccess')}</th>
                            <th className="px-10 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">{t('page-settings:users.role')}</th>
                            <th className="px-10 py-5 text-xs font-black text-gray-400 uppercase tracking-widest text-right">{t('common:actions')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {users.map((u) => (
                            <tr key={u.username} className="hover:bg-gray-50/30 transition-colors group">
                                <td className="px-10 py-6">
                                    <div className="text-sm font-black text-gray-900">{u.username}</div>
                                </td>
                                <td className="px-10 py-6">
                                    <div className="relative w-64">
                                        <select
                                            value={u.company_id || ''}
                                            onChange={(e) => handleUpdateUser(u.username, { company_id: e.target.value ? Number(e.target.value) : null })}
                                            className="w-full appearance-none bg-gray-50 border border-gray-100 rounded-xl px-4 py-2.5 text-sm font-bold text-gray-700 focus:ring-4 focus:ring-primary-500/5 outline-none pr-10"
                                        >
                                            <option value="">{t('page-settings:users.unassigned')}</option>
                                            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </select>
                                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    </div>
                                </td>
                                <td className="px-10 py-6">
                                    <div className="flex flex-wrap gap-2 max-w-xs">
                                        {u.role === 'admin' ? (
                                            <Badge variant="info">{t('page-settings:users.allCompanies')}</Badge>
                                        ) : (
                                            <>
                                                {(u.company_ids || []).map(cid => (
                                                    <div key={cid} className="flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-100 rounded-lg shadow-sm">
                                                        <span className="text-[10px] font-black text-gray-700">{companies.find(c => c.id === cid)?.name || cid}</span>
                                                        <button onClick={() => {
                                                            const next = u.company_ids.filter(id => id !== cid);
                                                            handleUpdateUser(u.username, { company_ids: next });
                                                        }} className="text-gray-400 hover:text-red-500 transition-colors">
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                ))}
                                                <div className="relative">
                                                    <button className="w-7 h-7 flex items-center justify-center bg-white border border-gray-100 rounded-lg text-gray-400 hover:text-primary-600 hover:border-primary-100 transition-all">
                                                        <Plus className="w-4 h-4" />
                                                    </button>
                                                    <select
                                                        className="absolute inset-0 opacity-0 cursor-pointer"
                                                        onChange={(e) => {
                                                            if (!e.target.value) return;
                                                            const cid = Number(e.target.value);
                                                            if (u.company_ids?.includes(cid)) return;
                                                            const next = [...(u.company_ids || []), cid];
                                                            handleUpdateUser(u.username, { company_ids: next });
                                                        }}
                                                    >
                                                        <option value="">{t('page-settings:users.addEllipsis')}</option>
                                                        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                                    </select>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </td>
                                <td className="px-10 py-6">
                                    <div className="relative w-44">
                                        <select
                                            value={u.role || 'viewer'}
                                            disabled={u.username === currentUsername && u.role === 'admin'}
                                            title={u.username === currentUsername && u.role === 'admin' ? t('page-settings:users.cannotChangeOwnAdminRole') : undefined}
                                            onChange={(e) => handleUpdateUser(u.username, { role: e.target.value })}
                                            className="w-full appearance-none bg-gray-50 border border-gray-100 rounded-xl px-4 py-2.5 text-sm font-bold text-gray-700 focus:ring-4 focus:ring-primary-500/5 outline-none pr-10 disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            <option value="admin">{t('page-settings:users.admin')}</option>
                                            <option value="editor">{t('page-settings:users.editor')}</option>
                                            <option value="viewer">{t('page-settings:users.viewer')}</option>
                                        </select>
                                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    </div>
                                </td>
                                <td className="px-10 py-6 text-right">
                                    {u.username !== currentUsername && (
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => setConfirmModal({ isOpen: true, id: u.username })}
                                        >
                                            {t('common:delete')}
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, id: null })}
                onConfirm={handleDeleteUser}
                title={t('page-settings:users.deleteTitle')}
                message={t('page-settings:users.deleteMessage')}
                confirmText={t('common:delete')}
            />
        </div>
    );
}

function DatabasesSection() {
    const { t } = useTranslation(['page-settings', 'common']);
    const [databases, setDatabases] = useState([]);
    const [companies, setCompanies] = useState([]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [dRes, cRes] = await Promise.all([adminListDatabases(), adminListCompanies()]);
            setDatabases(dRes.data?.data?.databases || []);
            setCompanies(cRes.data?.data?.companies || []);
        } catch (e) { }
    };

    const handleAssign = async (dbId, companyId) => {
        try {
            await adminAssignDatabaseCompany(dbId, { company_id: companyId ? Number(companyId) : null });
            fetchData();
        } catch (e) { }
    };

    return (
        <div className="p-10 animate-in fade-in duration-500">
            <div className="overflow-x-auto -mx-10">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50/50 border-y border-gray-100">
                            <th className="px-10 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">{t('common:name')}</th>
                            <th className="px-10 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">{t('common:type')}</th>
                            <th className="px-10 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">{t('page-settings:databases.company')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {databases.map((db) => (
                            <tr key={db.id} className="hover:bg-gray-50/30 transition-colors group">
                                <td className="px-10 py-6">
                                    <div className="text-sm font-black text-gray-900">{db.name}</div>
                                </td>
                                <td className="px-10 py-6">
                                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">{db.db_type}</div>
                                </td>
                                <td className="px-10 py-6">
                                    <div className="relative w-64">
                                        <select
                                            value={db.company_id || ''}
                                            onChange={(e) => handleAssign(db.id, e.target.value)}
                                            className="w-full appearance-none bg-gray-50 border border-gray-100 rounded-xl px-4 py-2.5 text-sm font-bold text-gray-700 focus:ring-4 focus:ring-primary-500/5 outline-none pr-10"
                                        >
                                            <option value="">{t('page-settings:databases.unassigned')}</option>
                                            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </select>
                                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
